import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/solid";
import { HttpError, useOne, useParse, useUpdate } from "@refinedev/core";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { get, isEmpty } from "lodash";
import { ISimpleEventDetail, ITicketsResponseData, IUsuallyTypeTicketForm, ShowTime, SupportedLanguage } from "interfaces";
import Button from "components/button";
import { SwitchButton } from "components/switchButton";
import { CreateEventStep } from "interfaces";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ConfirmModal } from "components/confirmModal";
import { Label } from "components/label";
import { isOpenRegistration } from "utills/helpers";
import { Showtimes } from "./showTimes";
import { Tickets } from "./tickets";

enum Section {
  SHOWTIMES = "showtimes",
  INDIVIDUAL_TICKET = "tickets",
  NONE = "none",
}

const CreateUsuallyTicketSchema = (languages: SupportedLanguage[]) => {
  return z.object({
    showtimes: z.array(
      z.object({
        time: z.string().nullable(),
        is_active: z.boolean(),
        hide_in_ui: z.boolean(),
        id: z.string(),
        created_at: z.string().nullable(),
        updated_at: z.string().nullable(),
      })
    ),
    tickets: z.array(z.any()),
  });
};

const INITIAL_ACTION_MODAL_CONFIG = {
  open: false,
  submit: () => {}
}

export const CreateUsuallyTypeTicket = (props: {eventDetailData: ISimpleEventDetail, refetchEventDetail: () => void}) => {

  const parse = useParse();
  const { params } = parse();

  const { data, isFetched, refetch: fetchTickets } =
  useOne<ITicketsResponseData>({
    resource: "content/events",
    id: `${params?.id}/tickets`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(params?.id && params?.step),
    } as any,
  });

  const defaultTicketsData = data?.data ?? null

  return isFetched ? (
    <CreateUsuallyTypeTicketForm
      defaultTicketsData={defaultTicketsData}
      fetchTickets={fetchTickets}
      {...props}
    />
  ) : <></>;
}

export const CreateUsuallyTypeTicketForm = ({eventDetailData, defaultTicketsData, fetchTickets, refetchEventDetail}: {eventDetailData: ISimpleEventDetail, defaultTicketsData: ITicketsResponseData | null, fetchTickets: () => void, refetchEventDetail: () => void}) => {
  const [openSectionId, setOpenSectionId] = useState<Section>(Section.NONE);
  const [enableShowtimeModalConfig, setEnableShowtimeModalConfig] = useState(INITIAL_ACTION_MODAL_CONFIG);

  const { mutate: updateMutation } = useUpdate();

  const parse = useParse();
  const { params } = parse();

  const methods = useForm<IUsuallyTypeTicketForm, HttpError, IUsuallyTypeTicketForm>({
    defaultValues: {
      showtimes: [],
      tickets: defaultTicketsData?.tickets ?? [],
    },
    mode: "onChange",
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        CreateUsuallyTicketSchema(eventDetailData.languages)
      );
      return createResolver(values, context, options);
    },
  });

  const { data: showTimesData, refetch: refetchShowTimes } = useOne<{showtimes: ShowTime[]}, HttpError>({
    resource: `content/events`,
    id: `attractions/${eventDetailData.id}/showtimes`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(params?.id && params?.step),
    } as any,
  });

  const showTimes = showTimesData?.data?.showtimes ?? null;

  useEffect(() => {
    if (showTimes) {
      methods.setValue("showtimes", (showTimes ?? []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTimes])

  const _toggleActiveShowTime = (isActive: boolean) => {
    updateMutation({
      resource: "content/events",
      id: `attractions/${eventDetailData.id}`,
      meta: {
        URLSuffix: `showtimes/${isActive ? "enable" : "disable"}`,
        ignoreResourceWrapper: true
      },
      values: {},
    },
    {
      onSuccess: () => {
        refetchShowTimes();
        toast.success(`Updated successfully`);
        fetchTickets();
        refetchEventDetail();
        if (!isActive && openSectionId === Section.SHOWTIMES) {
          setOpenSectionId(Section.NONE);
        }
      },
    });
  };

  const _handleSubmitSection = (sectionId: Section) => {
    if (!openSectionId) return setOpenSectionId(sectionId);
    return setOpenSectionId(
      openSectionId === sectionId ? Section.NONE : sectionId
    );
  };

  const _onCloseEnableShowtimeModal = () => {
    setEnableShowtimeModalConfig(INITIAL_ACTION_MODAL_CONFIG);
  }

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col h-full gap-y-2 sm:gap-y-4">
        <div className="box-container p-0">
          <div className="flex flex-col flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1 shrink-0 text-sm">
              <h3 className="text-base font-semibold leading-6">
                Showtimes
              </h3>
              <div className="mt-2 text-sm flex flex-wrap items-start gap-x-4">
                <Label 
                  id="showtimes"
                  name="Selling for multiple showtimes"
                  hint="You are not allowed to turn off the switch button if there is at least 1 showtime"
                />
                <SwitchButton 
                  checked={!isEmpty(methods.watch("showtimes") ?? [])}
                  disabled={!isEmpty(methods.watch("showtimes") ?? []) && (!isEmpty(defaultTicketsData?.tickets) || isOpenRegistration(get(eventDetailData, "open_registration_date", "")))}
                  onChange={(isCheck) => {
                    if (isCheck) {
                      setEnableShowtimeModalConfig({
                        open: true,
                        submit: () => _toggleActiveShowTime(true)
                      })
                    } else {
                      _toggleActiveShowTime(false)
                    }
                  }} 
                />
              </div>
            </div>
            {!isEmpty(methods.watch("showtimes")) ? (
              <button
                onClick={() => _handleSubmitSection(Section.SHOWTIMES)}
                type="button"
              >
                {openSectionId === Section.SHOWTIMES ? (
                  <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                ) : (
                  <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                )}
              </button>
            ) : <></>}
          </div>
          {openSectionId === Section.SHOWTIMES ? (
            <Showtimes 
              eventDetailData={eventDetailData}
              fetchShowTimes={refetchShowTimes}
            />
          ) : <></>}
        </div>

        <div className="box-container p-0">
          <div className="flex flex-col flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1 shrink-0">
              <h3 className="text-base font-semibold leading-6">
                Tickets
              </h3>
            </div>
            <button
              onClick={() => _handleSubmitSection(Section.INDIVIDUAL_TICKET)}
              type="button"
            >
              {openSectionId === Section.INDIVIDUAL_TICKET ? (
                <MinusCircleIcon className="w-6 h-6 text-brand-500" />
              ) : (
                <PlusCircleIcon className="w-6 h-6 text-brand-500" />
              )}
            </button>
          </div>
          {openSectionId === Section.INDIVIDUAL_TICKET ? (
            <Tickets 
              eventDetailData={eventDetailData} 
              showTimes={showTimes ?? []} 
              fetchEventDetail={refetchEventDetail} 
            />
           ) : <></>}
        </div>

        <div
          className="w-full mt-auto px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2]"
          style={{ boxShadow: "rgba(0, 0, 0, 0.23) 0px -5px 5px -5px" }}
        >
          <div className="flex items-center justify-end gap-x-4">
            <Link to={`/events/attractions/${params?.id}/edit?step=${CreateEventStep.PARTICIPANT_TEMPLATE}`}>
              <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                Back
              </Button>
            </Link>
            <Link
              to={`/events/attractions/${params?.id}/edit?step=${CreateEventStep.ADDITIONAL_SETUP}`}
            >
              <Button
                className={`w-20 disabled:opacity-70`}
                disabled={eventDetailData.current_step < params?.step}
              >
                Next
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <ConfirmModal 
        key="showtimes"
        title="Do you want to switch to using the showtime?"
        content="All tickets will automatically be assigned to the 00:00 showtime when show selling is enabled"
        open={enableShowtimeModalConfig.open}
        onConfirm={enableShowtimeModalConfig.submit}
        onClose={_onCloseEnableShowtimeModal}
      />
    </FormProvider>
  );
};
